// =====================================================================
//  Catálogo de funciones de la APP + acceso a las tablas de permisos/dock
// ---------------------------------------------------------------------
//  La BD solo guarda el ID de cada función (texto corto). Los detalles
//  visuales (nombre, ruta, ícono, color) viven AQUÍ en la APP, así el
//  backend queda simple y no hay que tocar Hojas Blancas para cambiar un
//  ícono o un texto.
//
//  Tablas (las crea la usuaria en NetCar; aquí solo se consumen):
//   1) T_ACC_FUNCIONESAPPUSUSUARIO  -> qué funciones puede VER el usuario
//   2) T_ACC_DOCKAPPUSUSUARIO       -> qué funciones puso el usuario en el
//                                      dock de abajo y en qué orden
//  Si cambias un nombre de tabla/columna en la BD, cámbialo también aquí.
// =====================================================================

import * as db from "./database.js";

// ---- Nombres EXACTOS de tablas/columnas (deben calzar con la BD) -----
// Las funciones se asignan por GRUPO/PERFIL (no por usuario). Un usuario
// puede pertenecer a varios grupos a través de T_ACC_USUARIO_R_GRUPO.
const T_FUNC          = "T_ACC_FUNCIONESAPPUSUSUARIO";
const C_FUNC_GRUPO    = "IN_COD_GRUPO";
const C_FUNC_FUNCION  = "ST_FUNCION_FUNCIONESAPPUSUSUARIO";
const C_FUNC_VIGENTE  = "IN_UNIONVIGENTE_FUNCIONESAPPUSUSUARIO";

// Relación grupo <-> usuario (un usuario puede tener 1 o más grupos)
const T_USUGRUPO      = "T_ACC_GRUPO_R_USUARIO";
const C_UG_USUARIO    = "IN_COD_USUARIO";
const C_UG_GRUPO      = "IN_COD_GRUPO";

const T_DOCK          = "T_ACC_DOCKAPPUSUSUARIO";
const C_DOCK_USUARIO  = "IN_COD_USUARIO";
const C_DOCK_FUNCION  = "ST_FUNCION_DOCKAPPUSUSUARIO";
const C_DOCK_ORDEN    = "IN_ORDEN_DOCKAPPUSUSUARIO";
const C_DOCK_VIGENTE  = "IN_UNIONVIGENTE_DOCKAPPUSUSUARIO";

// ---------------------------------------------------------------------
//  Catálogo maestro. El ID (ST_FUNCION) es lo que se guarda en la BD.
//  tone = familia de color del tema (sirve tanto para el cuadro del Home
//  como para el ícono del dock).
// ---------------------------------------------------------------------
const APP_FUNCTIONS = [
    { id: "BuscarOT",   name: "Buscar OT",    url: "/BuscarOT",   icon: "search",    tone: "aqua",     onclick: "localStorage.setItem('datasearch','');" },
    { id: "OTAgenda",   name: "OT Agenda",    url: "/OTAgenda",   icon: "calendar",  tone: "magenta1" },
    { id: "MisOT",      name: "Mis OTs",      url: "/MisOT",      icon: "briefcase", tone: "orange" },
    { id: "OTVencidas", name: "OTs Vencidas", url: "/OTVencidas", icon: "book-open", tone: "green1" },
    { id: "NewOT",      name: "Nueva OT",     url: "/NewOT",      icon: "plus",      tone: "blue2" }
];

// Funciones FIJAS del dock (siempre presentes, no se arrastran ni se quitan)
const FN_INICIO = { id: "Inicio", name: "Inicio", url: "/Dashboard", icon: "home",    tone: "red2",  fixed: true };
const FN_SALIR  = { id: "Salir",  name: "Salir",  url: "/login",     icon: "log-out", tone: "dark2", fixed: true };

// Dock por defecto (cuando el usuario aún no configuró nada)
const DEFAULT_DOCK = ["OTAgenda", "NewOT", "MisOT"];

// Cantidad máxima de íconos personalizables en el centro del dock
const DOCK_MAX = 4;

function getFunctionById(id) {
    if (id === FN_INICIO.id) return FN_INICIO;
    if (id === FN_SALIR.id)  return FN_SALIR;
    for (var i = 0; i < APP_FUNCTIONS.length; i++) {
        if (APP_FUNCTIONS[i].id === id) return APP_FUNCTIONS[i];
    }
    return null;
}

// getQuery devuelve OBJETO para 1 fila y ARREGLO para varias -> normalizar
function toArray(d) {
    if (d === null || d === undefined || d === "") return [];
    return Array.isArray(d) ? d : [d];
}

// Pequeño caché en memoria para no repetir la consulta de permisos
var _funcCache = {};

// ---------------------------------------------------------------------
//  Devuelve los IDs de función VIGENTES para el usuario.
//   - null  -> no hay datos (tabla vacía o aún no existe) => ver TODO
//   - [..]  -> el usuario solo ve esas funciones
// ---------------------------------------------------------------------
function getUserFunctions(codusuario) {
    if (_funcCache[codusuario]) return Promise.resolve(_funcCache[codusuario]);
    // Funciones VIGENTES de los grupos del usuario (DISTINCT: si dos grupos
    // tienen la misma función, aparece una sola vez).
    var sql = "SELECT DISTINCT " + C_FUNC_FUNCION + " FUNCION FROM " + T_FUNC +
              " WHERE " + C_FUNC_GRUPO + " IN (SELECT " + C_UG_GRUPO + " FROM " + T_USUGRUPO +
              " WHERE " + C_UG_USUARIO + "=" + codusuario + ")" +
              " AND " + C_FUNC_VIGENTE + "=1";
    return db.getQuery(sql).then(function (r) {
        if (!r || !r.data || r.data.err) return null; // tabla inexistente/erro -> ver todo
        var ids = toArray(r.data.data).map(function (x) { return x.FUNCION; }).filter(Boolean);
        var res = ids.length ? ids : null;             // sin filas -> ver todo
        _funcCache[codusuario] = res;
        return res;
    }).catch(function () { return null; });
}

// ---------------------------------------------------------------------
//  Devuelve el dock guardado del usuario (IDs en orden).
//   - null -> no configurado todavía => usar DEFAULT_DOCK
// ---------------------------------------------------------------------
function getUserDock(codusuario) {
    var sql = "SELECT " + C_DOCK_FUNCION + " FUNCION FROM " + T_DOCK +
              " WHERE " + C_DOCK_USUARIO + "=" + codusuario +
              " AND " + C_DOCK_VIGENTE + "=1 ORDER BY " + C_DOCK_ORDEN;
    return db.getQuery(sql).then(function (r) {
        if (!r || !r.data || r.data.err) return null;
        var ids = toArray(r.data.data).map(function (x) { return x.FUNCION; }).filter(Boolean);
        return ids.length ? ids : null;
    }).catch(function () { return null; });
}

// ---------------------------------------------------------------------
//  Guarda el dock del usuario: marca el anterior como no vigente e inserta
//  el nuevo con su orden. Un solo lote SQL via ExecuteQuery.
// ---------------------------------------------------------------------
function saveUserDock(codusuario, ids) {
    var sql = "UPDATE " + T_DOCK + " SET " + C_DOCK_VIGENTE + "=0 WHERE " +
              C_DOCK_USUARIO + "=" + codusuario + " AND " + C_DOCK_VIGENTE + "=1;";
    for (var i = 0; i < ids.length; i++) {
        sql += " INSERT INTO " + T_DOCK + " (" + C_DOCK_USUARIO + "," + C_DOCK_FUNCION + "," +
               C_DOCK_ORDEN + "," + C_DOCK_VIGENTE + ") VALUES (" +
               codusuario + ",'" + ids[i] + "'," + (i + 1) + ",1);";
    }
    return db.ExecuteQuery(sql).catch(function (e) {
        console.error("No se pudo guardar el dock:", e);
    });
}

export {
    APP_FUNCTIONS,
    FN_INICIO,
    FN_SALIR,
    DEFAULT_DOCK,
    DOCK_MAX,
    getFunctionById,
    getUserFunctions,
    getUserDock,
    saveUserDock
};
